import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, orderBy, query, documentId } from "firebase/firestore";
import { db } from "../firebase";
import { slugify } from "../slugify";
import { generalSpecsToRows } from "../context/CompareContext";
import { IMAGE_COMING_SOON } from "../imageComingSoon";
import "../pages/ComparePage.css";

const firstMatch = (fields, patterns) => {
  const entries = Object.entries(fields || {}).filter(([k]) => patterns.some((p) => p.test(k.trim())));
  const filled = entries.find(([, v]) => v !== "" && v != null);
  return (filled || entries[0] || ["", ""])[1] ?? "";
};

// One-shot read of every product in a Tixaru sub-category, shaped for the
// table. Same title-slug rule as ProductListingPage (a repeated title gets
// its doc id appended) so each column's "View Product" link resolves to the
// same detail page the grid links to. Returns [] until loaded or on error.
export function useSubcategoryItems(categorySlug, subCategorySlug) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cats = await getDocs(collection(db, "product_categories"));
        let cat = null;
        cats.forEach((d) => { if (slugify(d.data().categoryName) === categorySlug) cat = d; });
        if (!cat) return;
        const subs = await getDocs(collection(db, "product_categories", cat.id, "subCategories"));
        let sub = null;
        subs.forEach((d) => { if (slugify(d.data().subCategoryName || d.id) === subCategorySlug) sub = d; });
        if (!sub) return;
        const subName = sub.data().subCategoryName || sub.id;
        const snap = await getDocs(query(
          collection(db, "product_categories", cat.id, "subCategories", sub.id, "productData"),
          orderBy(documentId())
        ));
        const used = new Set();
        const out = [];
        snap.forEach((rowDoc) => {
          const f = rowDoc.data().fields || {};
          const title = firstMatch(f, [/^product.?name$/i, /^tit+le$/i, /^name$/i]) || subName;
          let slug = slugify(title);
          if (used.has(slug)) slug = `${slug}-${rowDoc.id.slice(0, 6).toLowerCase()}`;
          used.add(slug);
          const front = Object.entries(f).find(([k]) => /front.*image/i.test(k));
          const priceNum = parseFloat(firstMatch(f, [/^price$/i, /selling.?price/i, /^mrp$/i, /^rate$/i]));
          out.push({
            id: slug,
            partNo: firstMatch(f, [/part.?no/i, /part.?number/i, /model.?no/i]),
            title,
            image: (front && String(front[1] || "").split(",")[0].trim()) || IMAGE_COMING_SOON,
            price: isNaN(priceNum) ? null : priceNum,
            route: `/tft/${categorySlug}/${subCategorySlug}/${slug}`,
            specs: generalSpecsToRows(f.generalSpecs),
          });
        });
        if (!cancelled) setItems(out);
      } catch (err) {
        console.error(err);
      }
    })();
    return () => { cancelled = true; };
  }, [categorySlug, subCategorySlug]);
  return items;
}

// Side-by-side table of every product on a category page. Same look and
// item shape ({ id, partNo, title, image, price, route, specs }) as the
// Compare page, but fed from the whole listing instead of the visitor's
// picks, so there is nothing to remove and nothing to select.
export default function CategoryComparisonTable({ title, items, heading, hideLabels = [] }) {
  if (!items || items.length < 2) return null;

  // Union of every spec label across the products, in first-seen order; a
  // product without a given spec shows a dash in that row.
  const specLabels = [];
  const seen = new Set(hideLabels.map((l) => l.trim().toLowerCase()));
  items.forEach((item) => {
    (item.specs || []).forEach((s) => {
      const key = s.label.trim().toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        specLabels.push(s.label);
      }
    });
  });

  const valueFor = (item, label) => {
    const key = label.trim().toLowerCase();
    const match = (item.specs || []).find((s) => s.label.trim().toLowerCase() === key);
    return match && match.value ? match.value : "—";
  };

  return (
    <section className="category-compare" aria-label={`${title} comparison table`}>
      <h2 className="category-compare-title">{heading || `${title} Comparison`}</h2>
      <span className="compare-scroll-hint">Swipe to see more →</span>
      <div className="compare-table-scroll">
        <table className="compare-table">
          <thead>
            <tr>
              <th className="compare-row-label-col category-compare-corner">&nbsp;</th>
              {items.map((item) => (
                <th key={item.id} className="compare-col-head">
                  <img
                    src={item.image || IMAGE_COMING_SOON}
                    alt={item.title}
                    className="compare-col-image"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = IMAGE_COMING_SOON; }}
                  />
                  {item.partNo && <div className="compare-col-partno">{item.partNo}</div>}
                  <div className="compare-col-title">{item.title}</div>
                  <div className="compare-col-price">
                    {item.price != null ? `₹${Number(item.price).toLocaleString()}` : "Price: -"}
                  </div>
                  {item.route && (
                    <Link to={item.route} className="compare-col-view">View Product →</Link>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {specLabels.length === 0 ? (
              <tr>
                <td colSpan={items.length + 1} className="compare-no-specs">
                  Specifications for these products are coming soon.
                </td>
              </tr>
            ) : (
              specLabels.map((label) => (
                <tr key={label}>
                  <td className="compare-row-label category-compare-label">{label}</td>
                  {items.map((item) => (
                    <td key={item.id} className="compare-cell">
                      {valueFor(item, label)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
